import React, { useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FaSearch, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import { suggestProducts } from "../../api/productApi";
import { getProductImage } from "../RudrakshaSection/ProductCard";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import "./SearchBar.css";

const MIN_QUERY_LENGTH = 2;
const SUGGESTION_LIMIT = 6;

// Matches the backend's rule: 2+ characters, or a lone digit ("5" → 5 mukhi)
const isSearchable = (q) => q.length >= MIN_QUERY_LENGTH || /^\d$/.test(q);

const formatPrice = (price) => {
  const value = parseFloat(price);
  return Number.isFinite(value) ? `₹${value.toLocaleString("en-IN")}` : "";
};

/**
 * Header product search: debounced type-ahead suggestions from the whole
 * catalogue, with Enter / "View all results" going to the /search page.
 * `onNavigate` lets a container (e.g. the mobile drawer) close itself.
 */
const SearchBar = ({ className = "", onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listboxId = useId();

  const trimmed = query.trim();
  const debouncedQuery = useDebouncedValue(trimmed, 300);

  // Keep the box in sync with the results page's query (and clear it
  // when leaving that page) so the header always reflects what's shown.
  const pageQuery = location.pathname === "/search" ? searchParams.get("q") || "" : "";
  useEffect(() => {
    setQuery(pageQuery);
    setOpen(false);
  }, [pageQuery, location.pathname]);

  // Fetch suggestions for the debounced query; a newer query aborts the
  // previous request so out-of-order responses can't overwrite fresh ones.
  useEffect(() => {
    if (!isSearchable(debouncedQuery)) {
      setSuggestions([]);
      setStatus("idle");
      return undefined;
    }

    const controller = new AbortController();
    setStatus("loading");

    suggestProducts(debouncedQuery, { limit: SUGGESTION_LIMIT, signal: controller.signal })
      .then((items) => {
        setSuggestions(items);
        setActiveIndex(-1);
        setStatus("done");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        console.error("Search suggestions failed:", error);
        setSuggestions([]);
        setStatus("error");
      });

    return () => controller.abort();
  }, [debouncedQuery]);

  // Close on any click / tap outside the search box or dropdown. Capture
  // phase, because sliders on the page stop pointer events from bubbling.
  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [open]);

  const finish = () => {
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
    if (onNavigate) onNavigate();
  };

  const goToProduct = (product) => {
    navigate(`/product/${product.id}`);
    setQuery("");
    finish();
  };

  const goToResults = () => {
    if (!isSearchable(trimmed)) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    finish();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (open && activeIndex >= 0 && suggestions[activeIndex]) {
      goToProduct(suggestions[activeIndex]);
    } else {
      goToResults();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (!suggestions.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    }
  };

  const clearQuery = () => {
    setQuery("");
    setSuggestions([]);
    setStatus("idle");
    inputRef.current?.focus();
  };

  // Stale while typing: the debounced query hasn't caught up yet
  const isPending = trimmed !== debouncedQuery && isSearchable(trimmed);
  const showDropdown = open && isSearchable(trimmed);
  const optionId = (index) => `${listboxId}-option-${index}`;

  return (
    <div className={`site-search ${className}`} ref={wrapperRef}>
      <form className="searchBox" role="search" onSubmit={handleSubmit}>
        <FaSearch aria-hidden="true" />

        <input
          ref={inputRef}
          type="search"
          placeholder={t("search")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-label="Search products"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          autoComplete="off"
          enterKeyHint="search"
        />

        {query && (
          <button
            type="button"
            className="site-search-clear"
            onClick={clearQuery}
            aria-label="Clear search"
          >
            <FaTimes />
          </button>
        )}
      </form>

      {showDropdown && (
        <div className="site-search-dropdown">
          {(status === "loading" || isPending) && suggestions.length === 0 && (
            <div className="site-search-status">Searching…</div>
          )}

          {status === "error" && !isPending && (
            <div className="site-search-status">
              Couldn't load suggestions. Press Enter to search.
            </div>
          )}

          {status === "done" && !isPending && suggestions.length === 0 && (
            <div className="site-search-status">
              No products found for “{trimmed}”
            </div>
          )}

          {suggestions.length > 0 && (
            <ul
              id={listboxId}
              role="listbox"
              className={`site-search-results ${status === "loading" || isPending ? "is-stale" : ""}`}
            >
              {suggestions.map((item, index) => (
                <li
                  key={item.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`site-search-item ${index === activeIndex ? "active" : ""}`}
                  // Keep focus in the input while clicking a suggestion
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => goToProduct(item)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <img
                    src={getProductImage(item.image)}
                    alt=""
                    loading="lazy"
                    // Missing file: drop the src so the grey placeholder box shows
                    onError={(e) => e.currentTarget.removeAttribute("src")}
                  />

                  <div className="site-search-item-text">
                    <span className="site-search-item-name">{item.name}</span>
                    {item.category?.name && (
                      <span className="site-search-item-category">{item.category.name}</span>
                    )}
                  </div>

                  <div className="site-search-item-meta">
                    <span className="site-search-item-price">{formatPrice(item.price)}</span>
                    {item.status === "out_of_stock" && (
                      <span className="site-search-item-stock">Out of stock</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {(suggestions.length > 0 || status === "error") && (
            <button
              type="button"
              className="site-search-view-all"
              onMouseDown={(e) => e.preventDefault()}
              onClick={goToResults}
            >
              View all results for “{trimmed}”
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
